import { create } from "zustand";
import toast from "react-hot-toast";
import { RepositoryManager } from "../../data/repositoryManager";

export interface Category {
    id: number;
    name: string;
    display_name: string;
    description?: string | null;
    created_at: string;
    updated_at: string;
}

interface PaginationLinks {
    url: string | null;
    label: string;
    page: number | null;
    active: boolean;
}

interface CategoriesDataResponse {
    current_page: number;
    data: Category[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLinks[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
}

interface CategoryDataResponse {
    data: Category;
}

interface CategoriesStoreState {
    categoriesData: CategoriesDataResponse | null;
    categoryData: CategoryDataResponse | null;
    error: string | null;

    isListLoading: boolean;
    isDetailLoading: boolean;
    isSaving: boolean;
    isDeleting: boolean;

    getCategories: (params?: any) => Promise<{ success: boolean; data?: CategoriesDataResponse; error?: string }>;
    getCategoryById: (categoryId: string | number) => Promise<{ success: boolean; data?: CategoryDataResponse; error?: string }>;
    createCategory: (payload: Partial<Category>) => Promise<{ success: boolean; data?: Category; error?: string }>;
    updateCategory: (categoryId: string | number, payload: Partial<Category>) => Promise<{ success: boolean; data?: Category; error?: string }>;
    deleteCategory: (categoryId: string | number) => Promise<{ success: boolean; error?: string }>;
}

const useCategoriesStore = create<CategoriesStoreState>((set, get) => ({
    categoriesData: null,
    categoryData: null,
    error: null,

    isListLoading: false,
    isDetailLoading: false,
    isSaving: false,
    isDeleting: false,

    getCategories: async (params = {}) => {
        set({ isListLoading: true, error: null });
        try {
            const result = await RepositoryManager.categories.getCategories(params);
            if (result.success && result.data) {
                // Transform API response to match expected structure
                const transformedData = {
                    current_page: result.data.current_page || 1,
                    data: result.data.data || [],
                    first_page_url: "",
                    from: ((result.data.current_page || 1) - 1) * (result.data.per_page || 30) + 1,
                    last_page: result.data.last_page || 1,
                    last_page_url: "",
                    links: [],
                    next_page_url: null,
                    path: "",
                    per_page: result.data.per_page || 30,
                    prev_page_url: null,
                    to: (result.data.current_page || 1) * (result.data.per_page || 30),
                    total: result.data.total || 0,
                };
                set({ categoriesData: transformedData, isListLoading: false });
                return { success: true, data: transformedData };
            }
            throw new Error(result.message || "Failed to fetch categories");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isListLoading: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    getCategoryById: async (categoryId) => {
        set({ isDetailLoading: true, error: null });
        try {
            const result = await RepositoryManager.categories.getCategoryById(categoryId);
            if (result.success && result.data) {
                set({ categoryData: { data: result.data }, isDetailLoading: false });
                return { success: true, data: { data: result.data } };
            }
            throw new Error(result.message || "Failed to fetch category details");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isDetailLoading: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    createCategory: async (payload) => {
        set({ isSaving: true, error: null });
        try {
            const result = await RepositoryManager.categories.createCategory(payload);
            if (result.success && result.data) {
                const newCategory = result.data as Category;
                const { categoriesData } = get();
                if (categoriesData) {
                    const newData = {
                        ...categoriesData,
                        data: [newCategory, ...categoriesData.data],
                        total: categoriesData.total + 1,
                    };
                    set({ categoriesData: newData });
                }
                toast.success("Category created successfully ✅");
                set({ isSaving: false });
                return { success: true, data: newCategory };
            }
            throw new Error(result.message || "Failed to create category");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Error creating category";
            set({ isSaving: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    updateCategory: async (categoryId, payload) => {
        set({ isSaving: true, error: null });
        try {
            const result = await RepositoryManager.categories.updateCategory(categoryId, payload);
            if (result.success && result.data) {
                const updatedCategory = result.data as Category;
                const { categoriesData } = get();
                if (categoriesData) {
                    const newData = {
                        ...categoriesData,
                        data: categoriesData.data.map((c) => (c.id === updatedCategory.id ? updatedCategory : c)),
                    };
                    set({ categoriesData: newData });
                }
                toast.success("Category updated successfully ✅");
                set({ isSaving: false });
                return { success: true, data: updatedCategory };
            }
            throw new Error(result.message || "Failed to update category");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Error updating category";
            set({ isSaving: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    deleteCategory: async (categoryId) => {
        set({ isDeleting: true, error: null });
        try {
            const result = await RepositoryManager.categories.deleteCategory(categoryId);
            if (result.success) {
                const { categoriesData } = get();
                if (categoriesData) {
                    set({
                        categoriesData: {
                            ...categoriesData,
                            data: categoriesData.data.filter((c) => c.id !== categoryId),
                            total: categoriesData.total - 1,
                        },
                    });
                }
                toast.success("Category deleted successfully 🗑");
                set({ isDeleting: false });
                return { success: true };
            }
            throw new Error(result.message || "Failed to delete category");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Error deleting category";
            set({ isDeleting: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },
}));

export default useCategoriesStore;

